import AddIcon from '@material-design-icons/svg/filled/add.svg?react';
import InputIcon from '@material-design-icons/svg/filled/input.svg?react';
import NoteAddIcon from '@material-design-icons/svg/filled/note_add.svg?react';
import SettingsIcon from '@material-design-icons/svg/filled/settings.svg?react';
import { AnimatePresence, motion } from 'framer-motion';
import { useRef } from 'react';
import { useNavigate } from 'react-router';
import { usePreferences } from '../../contexts/PreferencesContext';
import { PUBLISHER_FROZEN_ACTION_TOOLTIP } from '../../utils/publisherFreeze';
import { hasConfiguredStorageProvider } from '../../utils/storageProviderUtils';
import { Button } from '../Button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

interface AddDappButtonProps {
  isAddDappDropdownOpen: boolean;
  onToggleDropdown: () => void;
  onImportDapp: () => void;
  isWalletConnected: boolean;
  isPublisherFrozen: boolean;
  isPublisherVerificationApproved: boolean;
  publisherVerificationMessage: string;
}

export const AddDappButton = ({
  isAddDappDropdownOpen,
  onToggleDropdown,
  onImportDapp,
  isWalletConnected,
  isPublisherFrozen,
  isPublisherVerificationApproved,
  publisherVerificationMessage,
}: AddDappButtonProps) => {
  const navigate = useNavigate();
  const { preferences } = usePreferences();
  const addDappDropdownRef = useRef<HTMLDivElement>(null);
  const addDappButtonRef = useRef<HTMLDivElement>(null);

  const isStorageProviderConfigured = hasConfiguredStorageProvider(preferences);
  const canAddDapp =
    isWalletConnected && !isPublisherFrozen && isPublisherVerificationApproved;
  const shouldPromptForStorageProvider =
    canAddDapp && !isStorageProviderConfigured;
  const isButtonDisabled = !canAddDapp;

  return (
    <div className="relative">
      <div className="px-2 py-4 flex justify-center">
        <div className="relative w-60 mx-auto">
          <div ref={addDappButtonRef}>
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <span className="block w-full">
                    <Button
                      onClick={
                        shouldPromptForStorageProvider
                          ? () => navigate('/dashboard/settings/preferences')
                          : onToggleDropdown
                      }
                      className={`group w-full h-10 py-3.5 transition-all duration-300 ease-in-out rounded-xl flex items-center relative ${
                        !isButtonDisabled
                          ? 'bg-white hover:shadow-[0_4px_16px_rgba(45,212,191,0.5)]'
                          : 'bg-gray-300 opacity-50 !cursor-default pointer-events-none'
                      }`}
                      variant="ghost"
                      disabled={isButtonDisabled}
                    >
                      <motion.div
                        animate={{
                          rotate:
                            isAddDappDropdownOpen && isStorageProviderConfigured
                              ? 45
                              : 0,
                        }}
                        transition={{
                          type: 'spring',
                          stiffness: 600,
                          damping: 25,
                        }}
                        className="absolute left-4"
                      >
                        {shouldPromptForStorageProvider ? (
                          <SettingsIcon className="text-black fill-current size-5" />
                        ) : (
                          <AddIcon
                            className={`${!isButtonDisabled ? 'text-black' : 'text-gray-600'} fill-current w-5 h-5`}
                          />
                        )}
                      </motion.div>
                      <div
                        className={`${!isButtonDisabled ? 'text-black' : 'text-gray-600'} text-sm font-bold leading-tight w-full text-center`}
                      >
                        {shouldPromptForStorageProvider
                          ? 'Set a Provider'
                          : 'Add a dApp'}
                      </div>
                    </Button>
                  </span>
                </TooltipTrigger>
                {isPublisherFrozen ? (
                  <TooltipContent>
                    <p>{PUBLISHER_FROZEN_ACTION_TOOLTIP}</p>
                  </TooltipContent>
                ) : !isPublisherVerificationApproved ? (
                  <TooltipContent>
                    <p>{publisherVerificationMessage}</p>
                  </TooltipContent>
                ) : !isWalletConnected ? (
                  <TooltipContent>
                    <p>Connect a wallet to add a dApp</p>
                  </TooltipContent>
                ) : !isStorageProviderConfigured ? (
                  <TooltipContent>
                    <p>Set up a storage provider to upload dApp assets</p>
                  </TooltipContent>
                ) : null}
              </Tooltip>
            </TooltipProvider>
          </div>

          <AnimatePresence mode="wait">
            {isAddDappDropdownOpen &&
              isStorageProviderConfigured &&
              canAddDapp && (
                <motion.div
                  ref={addDappDropdownRef}
                  className="absolute bottom-full mb-2 left-0 right-0 flex flex-col gap-1 z-50"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <motion.button
                    onClick={() => {
                      navigate('/dashboard/new-dapp');
                      onToggleDropdown();
                    }}
                    className="w-full h-10 px-4 py-3.5 bg-white/10 backdrop-blur-sm rounded-xl flex items-center relative hover:bg-white/20 transition-colors duration-150"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{
                      type: 'spring',
                      stiffness: 500,
                      damping: 30,
                      mass: 0.8,
                      delay: 0.05,
                    }}
                    whileTap={{ opacity: 0.9 }}
                  >
                    <NoteAddIcon className="text-white fill-current size-5 absolute left-4" />
                    <div className="text-white text-sm font-bold leading-tight w-full text-center">
                      New Dapp
                    </div>
                  </motion.button>
                  <motion.button
                    onClick={() => {
                      onImportDapp();
                      onToggleDropdown();
                    }}
                    className="w-full h-10 px-4 py-3.5 bg-white/10 backdrop-blur-sm rounded-xl flex items-center relative hover:bg-white/20 transition-colors duration-150"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{
                      type: 'spring',
                      stiffness: 500,
                      damping: 30,
                      mass: 0.8,
                    }}
                    whileTap={{ opacity: 0.9 }}
                  >
                    <InputIcon className="text-white fill-current size-5 absolute left-4" />
                    <div className="text-white text-sm font-bold leading-tight w-full text-center">
                      Import Dapp
                    </div>
                  </motion.button>
                </motion.div>
              )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
